import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./CommentsSheet.css";

/**
 * Comments surface — inline on laptop/desktop, a bottom sheet on tablet/phone.
 *
 * WHY TWO SHAPES. Inline comments work on a wide screen: the thread sits under
 * the post and there is room either side of it. On a handset the same markup
 * has to fit a two-level thread, an avatar column per level and a composer into
 * ~340px, and it pushes the post it belongs to off the top of the screen while
 * you read. Every app that does this well (the reference here is Instagram)
 * opens comments as a sheet instead: the post stays put behind a scrim, the
 * thread gets the full width, and the composer is pinned to the bottom within
 * thumb reach instead of drifting down the page as the list grows.
 *
 * The breakpoint is 1024px, matching the app's own `lg:hidden` bottom tab bar —
 * exactly the widths that already behave as "compact".
 *
 * The caller passes the thread as `children` and the composer as `footer`. They
 * are separate props because in the sheet the composer must NOT scroll with the
 * list; inline it simply renders after it, as before.
 */

/** True while the viewport is tablet-or-narrower. Live — it follows a rotate. */
export function useCompactViewport(query = "(max-width: 1023px)") {
  const [compact, setCompact] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setCompact(e.matches);
    setCompact(mq.matches); // re-sync in case it changed before the listener
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return compact;
}

export default function CommentsSheet({
  compact,
  onClose,
  title = "Comments",
  count,
  footer = null,
  children,
}) {
  // Drag-to-dismiss. A grab handle that cannot be grabbed is a lie, so the
  // header tracks a downward drag and closes past a threshold; anything shorter
  // springs back. Vertical only — a horizontal swipe belongs to the carousel
  // behind the sheet.
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startY = useRef(0);

  const onTouchStart = useCallback((e) => {
    startY.current = e.touches[0].clientY;
    setDragging(true);
  }, []);
  const onTouchMove = useCallback((e) => {
    const dy = e.touches[0].clientY - startY.current;
    setDragY(dy > 0 ? dy : 0); // never drag it above its resting position
  }, []);
  const onTouchEnd = useCallback(() => {
    setDragging(false);
    setDragY((dy) => {
      if (dy > 90) onClose?.();
      return 0;
    });
  }, [onClose]);

  // Escape closes, like every other overlay in the app.
  useEffect(() => {
    if (!compact) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [compact, onClose]);

  if (!compact) {
    // Laptop/desktop — byte-for-byte the previous inline layout.
    return (
      <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
        {children}
        {footer}
      </div>
    );
  }

  // Portalled to <body>: the post card is inside overflow/transform contexts
  // that would otherwise clip a fixed child, and the backdrop needs to cover
  // the whole viewport rather than the card.
  return createPortal(
    <>
      {/* `fixed inset-0` with z >= 40 is what ScrollLockManager watches for, so
          the page behind freezes without this component opting in. */}
      <div
        className="comments-sheet__scrim fixed inset-0 z-[9998] bg-ink/45"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="comments-sheet fixed inset-x-0 bottom-0 z-[9999] flex max-h-[85dvh] flex-col overflow-hidden rounded-t-[22px] bg-surface"
        style={{
          transform: dragY ? `translate3d(0, ${dragY}px, 0)` : undefined,
          transition: dragging ? "none" : undefined,
        }}
      >
        {/* Grab handle + title. The whole strip is the drag target, not just
            the 40px pill — a 4px-tall pill is far too small to aim at. */}
        <div
          className="shrink-0 cursor-grab touch-none select-none active:cursor-grabbing"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="grid place-items-center pb-1 pt-2.5">
            <span className="h-1 w-10 rounded-full bg-gray-300" />
          </div>
          <div className="relative border-b border-gray-100 pb-3 pt-1">
            <h3 className="text-center text-[15px] font-bold tracking-[-0.01em] text-gray-900">
              {title}
              {count > 0 ? (
                <span className="ml-1.5 font-semibold text-gray-400">{count}</span>
              ) : null}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close comments"
              className="absolute right-2.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            >
              <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* The thread. `min-h-0` is what lets this shrink inside the flex column
            so the footer stays pinned; without it the body grows to its content
            and pushes the composer off the bottom of the sheet. */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-3.5">
          {children}
        </div>

        {footer ? (
          <div
            className="shrink-0 border-t border-gray-100 bg-surface px-3.5 pt-3"
            /* Clears the home indicator on a phone; falls back to a plain 12px
               on anything without a safe area. */
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </>,
    document.body,
  );
}
